// ===== Manage tab — rename / delete titles & creatives (소재 관리) =====
// Library tree from session.titles; mutations go through library.js (API-backed).

import { escapeHtml, toast } from './util.js';
import { titles } from './session.js';
import { openBackdrop, closeBackdrop } from './ui/modal.js';
import {
  renameTitle, deleteTitle, renameCreative, deleteCreative,
  renameFile, deleteFile, renameCanvas, deleteCanvas, updateSelectBarSub,
} from './library.js';

const root = document.getElementById('manageRoot');

const PENCIL = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>`;

// Cascade counts for the delete warning.
function fileCanvasCount(f) { return (f.canvases || []).length; }
function creativeCounts(c) {
  const files = (c.files || []).length;
  const canvases = (c.files || []).reduce((s, f) => s + fileCanvasCount(f), 0);
  return { files, canvases };
}
function titleCounts(t) {
  let creatives = t.creatives.length, files = 0, canvases = 0;
  for (const c of t.creatives) { const n = creativeCounts(c); files += n.files; canvases += n.canvases; }
  return { creatives, files, canvases };
}
const TRASH = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;

function render() {
  if (!root) return;
  const head = `<div class="cv-head"><h2>소재 관리</h2>
    <p>타이틀·대분류·소분류·캔버스의 이름을 바꾸거나 삭제합니다. 삭제 시 하위 항목까지 함께 제거됩니다.</p></div>`;
  if (!titles.length) {
    root.innerHTML = head + `<div class="cv-note">아직 업로드된 소재가 없습니다. Home에서 HTML을 업로드하세요.</div>`;
    return;
  }
  root.innerHTML = head + `<div class="mg-tree" id="mgTree"></div>`;
  const tree = root.querySelector('#mgTree');
  titles.forEach((t) => tree.appendChild(renderTitle(t)));
}

function renderTitle(t) {
  const block = document.createElement('div');
  block.className = 'mg-title-block';
  const tc = titleCounts(t);
  block.appendChild(renderRow({
    kind: 'title',
    name: t.name,
    meta: `${t.creatives.length}개 대분류`,
    onRename: (name) => renameTitle(t, name),
    onDelete: () => deleteTitle(t),
    danger: {
      kindLabel: '타이틀',
      cascade: `이 타이틀과 하위 <b>${tc.creatives}개 대분류 · ${tc.files}개 소분류 · ${tc.canvases}개 캔버스</b>가 모두 영구 삭제됩니다.`,
    },
  }));

  const cwrap = document.createElement('div');
  cwrap.className = 'mg-creatives';
  if (!t.creatives.length) {
    const e = document.createElement('div');
    e.className = 'mg-empty';
    e.textContent = '대분류 없음';
    cwrap.appendChild(e);
  } else {
    t.creatives.forEach((c) => cwrap.appendChild(renderCreative(t, c)));
  }
  block.appendChild(cwrap);
  return block;
}

function renderCreative(t, c) {
  const block = document.createElement('div');
  block.className = 'mg-creative-block';
  const cc = creativeCounts(c);
  block.appendChild(renderRow({
    kind: 'creative',
    name: c.name,
    meta: `${(c.files || []).length}개 소분류`,
    onRename: (name) => renameCreative(c, name),
    onDelete: () => deleteCreative(t, c),
    danger: {
      kindLabel: '대분류',
      cascade: `이 대분류와 하위 <b>${cc.files}개 소분류 · ${cc.canvases}개 캔버스</b>가 모두 영구 삭제됩니다.`,
    },
  }));

  const fwrap = document.createElement('div');
  fwrap.className = 'mg-files';
  if (!(c.files || []).length) {
    const e = document.createElement('div');
    e.className = 'mg-empty';
    e.textContent = '소분류 없음';
    fwrap.appendChild(e);
  } else {
    c.files.forEach((f) => fwrap.appendChild(renderFile(c, f)));
  }
  block.appendChild(fwrap);
  return block;
}

function renderFile(c, f) {
  const block = document.createElement('div');
  block.className = 'mg-file-block';
  block.appendChild(renderRow({
    kind: 'file',
    name: f.name,
    meta: `${(f.canvases || []).length}개 캔버스`,
    onRename: (name) => renameFile(f, name),
    onDelete: () => deleteFile(c, f),
    danger: {
      kindLabel: '소분류',
      cascade: `이 소분류(base 빌드)와 하위 <b>${(f.canvases || []).length}개 캔버스</b>가 모두 영구 삭제됩니다.`,
    },
  }));

  const cvwrap = document.createElement('div');
  cvwrap.className = 'mg-canvases';
  if (!(f.canvases || []).length) {
    const e = document.createElement('div');
    e.className = 'mg-empty';
    e.textContent = '캔버스 없음';
    cvwrap.appendChild(e);
  } else {
    f.canvases.forEach((cv) => cvwrap.appendChild(renderRow({
      kind: 'canvas',
      name: cv.name,
      meta: `${(cv.tw || []).length + Object.keys(cv.pkg || {}).length}개 오버라이드`,
      onRename: (name) => renameCanvas(cv, name),
      onDelete: () => deleteCanvas(f, cv),
    })));
  }
  block.appendChild(cvwrap);
  return block;
}

function renderRow({ kind, name, meta, onRename, onDelete, danger }) {
  const row = document.createElement('div');
  row.className = `mg-row mg-${kind}`;

  const label = document.createElement('div');
  label.className = 'mg-label';
  label.innerHTML = `<span class="mg-name">${escapeHtml(name)}</span><span class="mg-meta">${escapeHtml(meta)}</span>`;
  row.appendChild(label);

  const actions = document.createElement('div');
  actions.className = 'mg-actions';

  const renameBtn = document.createElement('button');
  renameBtn.type = 'button';
  renameBtn.className = 'mg-btn';
  renameBtn.title = '이름 변경';
  renameBtn.innerHTML = PENCIL;
  renameBtn.addEventListener('click', () => startRename(row, name, onRename));
  actions.appendChild(renameBtn);

  const runDelete = () => {
    Promise.resolve(onDelete()).catch((e) => { console.warn('[manage] delete failed:', e.message); toast('삭제 실패'); });
    updateSelectBarSub();
    render();
  };

  const delBtn = document.createElement('button');
  delBtn.type = 'button';
  delBtn.className = 'mg-btn mg-del';
  delBtn.title = '삭제';
  delBtn.innerHTML = TRASH;

  if (danger) {
    // Cascading delete (title/대분류/소분류) → red type-to-confirm modal.
    delBtn.addEventListener('click', () => openDeleteModal({
      kindLabel: danger.kindLabel, name, cascade: danger.cascade, onConfirm: runDelete,
    }));
  } else {
    // Leaf delete (canvas) → quick inline 2-step confirm.
    let armed = false, timer = null;
    delBtn.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        delBtn.classList.add('confirm');
        delBtn.textContent = '삭제?';
        timer = setTimeout(() => { armed = false; delBtn.classList.remove('confirm'); delBtn.innerHTML = TRASH; }, 2500);
        return;
      }
      if (timer) clearTimeout(timer);
      runDelete();
    });
  }
  actions.appendChild(delBtn);

  row.appendChild(actions);
  return row;
}

function startRename(row, current, onRename) {
  const label = row.querySelector('.mg-label');
  if (!label) return;
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'mg-rename-input';
  input.value = current;
  const commit = () => {
    const n = input.value.trim();
    if (n && n !== current) Promise.resolve(onRename(n)).catch((e) => { console.warn('[manage] rename failed:', e.message); toast('이름 변경 실패'); });
    render();
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { e.preventDefault(); render(); }
  });
  input.addEventListener('blur', commit);
  label.replaceWith(input);
  input.focus();
  input.select();
}

// ===== Delete-confirm modal (type-to-confirm) =====
const deEls = {
  head: document.getElementById('deleteHead'),
  cascade: document.getElementById('deleteCascade'),
  target: document.getElementById('deleteTargetName'),
  input: document.getElementById('deleteInput'),
  mismatch: document.getElementById('deleteMismatch'),
  confirm: document.getElementById('deleteConfirm'),
  crumb: document.getElementById('deleteCrumb'),
};
let pendingDelete = null;

function openDeleteModal({ kindLabel, name, cascade, onConfirm }) {
  pendingDelete = { name, onConfirm };
  deEls.head.textContent = `${kindLabel} 영구 삭제`;
  deEls.cascade.innerHTML = cascade;
  deEls.target.textContent = name;
  deEls.crumb.textContent = name;
  deEls.input.value = '';
  deEls.confirm.disabled = true;
  deEls.mismatch.classList.remove('show');
  openBackdrop('deleteModal');
  setTimeout(() => deEls.input.focus(), 80);
}

function syncDeleteInput() {
  const v = deEls.input.value.trim();
  const match = !!pendingDelete && v === pendingDelete.name;
  deEls.confirm.disabled = !match;
  deEls.mismatch.classList.toggle('show', v.length > 0 && !match);
}

function commitDelete() {
  if (!pendingDelete) return;
  if (deEls.input.value.trim() !== pendingDelete.name) return;
  const fn = pendingDelete.onConfirm;
  pendingDelete = null;
  closeBackdrop('deleteModal');
  fn();
}

deEls.input?.addEventListener('input', syncDeleteInput);
deEls.input?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') { e.preventDefault(); commitDelete(); }
});
deEls.confirm?.addEventListener('click', commitDelete);

// Lazy: (re)render whenever the Manage tab is opened (fresh library state).
document.querySelector('#homeTabs a[data-home-tab="manage"]')?.addEventListener('click', render);
